import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { useLocation, useNavigate } from 'react-router-dom'
import cn from 'classnames'

import { BabyGender } from 'types'
import { FlexLayout } from 'layouts'
import { analyticsContext, useValueContext, ValueContext } from 'contexts'
import {
  BottleSmileIcon,
  DotsLoader,
  HomeIcon,
  PageSpinner,
  PinIcon,
  SleepSmileIcon,
  StepTooltip,
} from 'components'
import {
  calculateAge,
  formatDurationFromSeconds,
  isDefAndNotEmpty,
  useApp,
  useInfiniteScroll,
} from 'utils'

import { CurrentEventsList, EventsList, EventTypes, NoEvents } from './components'
import './styles.scss'

const mobileTooltipJsx = (
  <>
    <div style={{ marginBottom: 8 }}>Нужен быстрый доступ к приложению? Легко!</div>
    <div style={{ display: 'flex', gap: 4 }}>
      <HomeIcon width={10} height={10} />
      <span>
        На телефоне выберите в меню пункт{' '}
        <span style={{ fontWeight: 600 }}>“Добавить на экран Домой”</span>
      </span>
    </div>
    <div style={{ display: 'flex', gap: 4 }}>
      <PinIcon width={10} height={10} /> Закрепите в Telegram чат с ботом, чтобы он всегда был на
      виду
    </div>
  </>
)

export const Home = observer(() => {
  const navigate = useNavigate()
  const { openFirstOnboardingTooltip } = useApp()
  const { state } = useLocation()
  const { babyStore, homeStore } = useValueContext()
  const {
    babies,
    selectedBaby: baby,
    lastEvents,
    loaded: babiesLoaded,
    lastEventsLoading,
    getBabyLastEvents,
  } = babyStore

  const { events, setBaby, loadCurrentEvents, loading, loaded, pagination, loadMoreEvents } =
    homeStore

  const showNoData =
    (loaded && !isDefAndNotEmpty(events)) || (babiesLoaded && !isDefAndNotEmpty(babies))

  useEffect(() => {
    if (baby) {
      setBaby(baby)
      loadCurrentEvents()
      getBabyLastEvents()
    }
  }, [baby, setBaby, loadCurrentEvents, getBabyLastEvents])

  useEffect(() => {
    if (pagination.currentPage > 1) loadMoreEvents()
  }, [pagination.currentPage, loadMoreEvents])

  useEffect(() => {
    if (state?.showTooltips) {
      openFirstOnboardingTooltip()
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [state, openFirstOnboardingTooltip])

  const onScrollToBottom = () => pagination.setNextPage()
  const onScroll = useInfiniteScroll(homeStore.loadingMore, onScrollToBottom)
  const age = calculateAge(baby?.birthDate)

  const onAddEvent = () => {
    analyticsContext.trackEvent('plus_button_clicked')
    navigate('/new-event')
  }

  return (
    <ValueContext.Provider value={{ store: homeStore, baby }}>
      <PageSpinner delay={400} minVisibleTime={400} open={loading} />
      <FlexLayout.Container className="home-screen with-mobile-indent with-menu-padding" vertical>
        <StepTooltip
          step={4}
          right={4}
          top="calc(4px + var(--mobile-top-indent))"
          width={230}
          position="bottom-left"
          className="mobile-tooltip"
          content={mobileTooltipJsx}
        />
        <div className="child-info">
          <div className="child-title">
            <div
              className={cn('name', {
                male: baby?.gender === BabyGender.MALE,
                female: baby?.gender === BabyGender.FEMALE,
              })}
            >
              {baby?.name}
            </div>
            {age && <div className="age">{age}</div>}
          </div>
          <div className="time-info">
            <div className="time feed-time">
              <BottleSmileIcon />{' '}
              {lastEventsLoading ? (
                <DotsLoader className="time-loader" />
              ) : (
                <span className="duration-time">
                  {formatDurationFromSeconds(lastEvents?.lastFeedingSeconds)}
                </span>
              )}
              <StepTooltip
                step={2}
                left={-110}
                top={35}
                width={220}
                position="bottom-left"
                content="Следите за временем, которое прошло с предыдущего кормления и сна.
Получайте напоминания в Telegram"
              />
            </div>
            <div className="time sleep-time">
              <SleepSmileIcon />{' '}
              {lastEventsLoading ? (
                <DotsLoader className="time-loader" />
              ) : (
                <span className="duration-time">
                  {formatDurationFromSeconds(lastEvents?.lastSleepSeconds)}
                </span>
              )}
            </div>
          </div>
        </div>
        <EventTypes />
        <CurrentEventsList />
        <FlexLayout.Content className="scrollable-layout" scrollable onScroll={onScroll}>
          {showNoData ? <NoEvents /> : <EventsList />}
        </FlexLayout.Content>
        <button className="add-event-btn" disabled={!baby} onClick={onAddEvent}>
          +
        </button>
      </FlexLayout.Container>
    </ValueContext.Provider>
  )
})
